import React from 'react';
import { Modal, Button as BootstrapButton } from 'react-bootstrap';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { colorAccent, colorBlue } from '@spark/ui-components/theme/palette';
import styled from 'styled-components';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useVeiculosListagem, useVeiculoNavegacao } from '../hooks';
import VeiculoHeader from './VeiculoHeader';
import VeiculoTable from './VeiculoTable';
import VeiculoFilter from './VeiculoFilter';

const VeiculoListCard = ({ item }) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <VehicleCardWrap>
      <VehicleCardHeader>
        <VehicleMainInfo>
          <VehicleTitle>{item.placa || '-'}</VehicleTitle>
          <VehicleSubtitle>{item.frota || '-'}</VehicleSubtitle>
        </VehicleMainInfo>

        <VehicleExpandButton type="button" onClick={() => setExpanded(prev => !prev)} aria-label="Expandir card">
          <ExpandMoreIcon
            style={{
              fontSize: 20,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}
          />
        </VehicleExpandButton>
      </VehicleCardHeader>

      <VehicleMetaRow>
        <VehicleMetaItem>
          <VehicleMetaLabel>Descricao:</VehicleMetaLabel>
          <VehicleMetaValue>{item.descricao || '-'}</VehicleMetaValue>
        </VehicleMetaItem>
        <VehicleMetaItem>
          <VehicleMetaLabel>UF:</VehicleMetaLabel>
          <VehicleMetaValue>{item.uf || '-'}</VehicleMetaValue>
        </VehicleMetaItem>
        <VehicleMetaItem>
          <VehicleMetaLabel>Situacao:</VehicleMetaLabel>
          <VehicleMetaValue>{item.situacao === 1 ? 'Ativo' : 'Inativo'}</VehicleMetaValue>
        </VehicleMetaItem>
      </VehicleMetaRow>

      {expanded ? (
        <VehicleExpandedSection>
          <VehicleMetaRow>
            <VehicleMetaItem>
              <VehicleMetaLabel>Renavam:</VehicleMetaLabel>
              <VehicleMetaValue>{item.renavam || '-'}</VehicleMetaValue>
            </VehicleMetaItem>
            <VehicleMetaItem>
              <VehicleMetaLabel>Tipo Modelo:</VehicleMetaLabel>
              <VehicleMetaValue>{item.nomeTipoModeloVeicular || '-'}</VehicleMetaValue>
            </VehicleMetaItem>
            <VehicleMetaItem>
              <VehicleMetaLabel>Capacidade:</VehicleMetaLabel>
              <VehicleMetaValue>{item.capacidadeModelo ?? '-'}</VehicleMetaValue>
            </VehicleMetaItem>
            <VehicleMetaItem>
              <VehicleMetaLabel>Eixos:</VehicleMetaLabel>
              <VehicleMetaValue>{item.numeroEixos ?? '-'}</VehicleMetaValue>
            </VehicleMetaItem>
          </VehicleMetaRow>
        </VehicleExpandedSection>
      ) : null}
    </VehicleCardWrap>
  );
};

const VeiculoCard = ({
  id,
  showActions = true,
  enableRowClick = true,
  showFilter = true,
  showHeader = true,
}) => {
  const {
    data,
    loading,
    filterLoading,
    totalRegistros,
    paginaAtual,
    quantidadePorPagina,
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    confirmDelete,
    cancelDelete,
    showDeleteModal,
    veiculoToDelete,
    setPaginaAtual,
    setQuantidadePorPagina,
  } = useVeiculosListagem(id);

  const { handleRowClick, handleAdicionar, handleEditar } = useVeiculoNavegacao();

  return (
    <>
      <Card>
        <CardBody>
          {showHeader && (
            <VeiculoHeader
              totalRegistros={totalRegistros}
              onAdicionar={showActions ? handleAdicionar : undefined}
            />
          )}

          {showFilter && (
            <VeiculoFilter
              loading={filterLoading}
              onApply={handleFilterApply}
              onLimpar={handleLimparFiltros}
            />
          )}

          <VeiculoTable
            data={data}
            loading={loading}
            onRowClick={enableRowClick ? handleRowClick : undefined}
            onEditar={showActions ? handleEditar : undefined}
            onExcluir={showActions ? handleExcluir : undefined}
            manualPagination
            totalRegistros={totalRegistros}
            controlledPagination={{
              pageIndex: Math.max(0, (paginaAtual || 1) - 1),
              pageSize: quantidadePorPagina || 10,
            }}
            enableCardView={true}
            defaultViewMode="table"
            renderCard={item => <VeiculoListCard item={item} />}
            onPaginationChange={(next) => {
              if (next && typeof next.pageSize === 'number') {
                setQuantidadePorPagina(next.pageSize);
              }
              if (next && typeof next.pageIndex === 'number') {
                setPaginaAtual(next.pageIndex + 1);
              }
            }}
          />
        </CardBody>
      </Card>

      <Modal show={showDeleteModal} onHide={cancelDelete} animation={false} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar exclusao</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Tem certeza que deseja excluir o veiculo{' '}
            <strong>{veiculoToDelete?.placa || veiculoToDelete?.frota || veiculoToDelete?.id}</strong>?
          </p>
          <p className="text-muted">Esta acao nao pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={cancelDelete}>
            Cancelar
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmDelete}>
            Excluir
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VeiculoCard;

const VehicleCardWrap = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  background: linear-gradient(90deg, ${colorBlue} 0%, ${colorAccent} 100%);
  color: #ffffff;
`;

const VehicleCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const VehicleMainInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const VehicleTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
`;

const VehicleSubtitle = styled.div`
  font-size: 12px;
  opacity: 0.9;
  margin-top: 2px;
`;

const VehicleExpandButton = styled.button`
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const VehicleMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 8px;
`;

const VehicleMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const VehicleMetaLabel = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.72;
`;

const VehicleMetaValue = styled.span`
  font-size: 12px;
  font-weight: 600;
`;

const VehicleExpandedSection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;
